import { useState } from "react";
import { Button, Card, Form, Input, Space, message } from "antd";
import { ArrowLeftOutlined, LockOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useGetIdentity } from "@refinedev/core";

import { profileService } from "../../services/auth/profileService";
import { usePageTitle } from "../../hooks";
import { PageHeader } from "../../components/common/PageHeader";
import type { UserInfo } from "../../types/auth";
import { ADMIN_USER_TYPES } from "../../types/auth";

interface FormValues {
  old_password: string;
  new_password: string;
  new_password_confirm: string;
}

export const AuditorChangePassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: identity } = useGetIdentity<UserInfo>();
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);
  // Componente reutilizado también en el área admin (`/admin/profile/password`).
  // La ruta de vuelta al perfil depende del rol del usuario.
  const isAdmin = (ADMIN_USER_TYPES as readonly number[]).includes(
    identity?.app_user?.user_type ?? -1,
  );
  const profilePath = isAdmin ? "/admin/profile" : "/auditor/profile";

  usePageTitle(t("profile.password.title"));

  const onSubmit = async (values: FormValues) => {
    setSubmitting(true);
    try {
      await profileService.changePassword(values);
      message.success(t("profile.password.success"));
      form.resetFields();
      navigate(profilePath);
    } catch (err: any) {
      const data = err?.response?.data ?? err?.originalError?.response?.data;
      if (data?.old_password) {
        message.error(t("profile.password.wrong_old"));
      } else if (data?.new_password) {
        const msg = Array.isArray(data.new_password)
          ? data.new_password[0]
          : String(data.new_password);
        message.error(msg);
      } else if (data?.new_password_confirm) {
        message.error(t("profile.password.mismatch"));
      } else {
        message.error(t("profile.password.error"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <PageHeader
        icon={<LockOutlined />}
        accent="#722ED1"
        title={t("profile.password.title")}
        extra={
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(profilePath)}
            style={{ borderRadius: 999, paddingInline: 16 }}
          >
            {t("profile.back_to_profile")}
          </Button>
        }
      />
      <Space
        direction="vertical"
        size="middle"
        style={{ width: "100%" }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onSubmit}
          requiredMark={false}
        >
          <Form.Item
            name="old_password"
            label={t("profile.password.old_password")}
            rules={[{ required: true }]}
          >
            <Input.Password
              autoComplete="current-password"
              placeholder={t("profile.password.ph_old")}
            />
          </Form.Item>
          <Form.Item
            name="new_password"
            label={t("profile.password.new_password")}
            rules={[
              { required: true },
              { min: 8, message: t("profile.password.min_length") },
            ]}
          >
            <Input.Password
              autoComplete="new-password"
              placeholder={t("profile.password.ph_new")}
            />
          </Form.Item>
          <Form.Item
            name="new_password_confirm"
            label={t("profile.password.new_password_confirm")}
            dependencies={["new_password"]}
            rules={[
              { required: true },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("new_password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t("profile.password.mismatch")));
                },
              }),
            ]}
          >
            <Input.Password
              autoComplete="new-password"
              placeholder={t("profile.password.ph_confirm")}
            />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            icon={<LockOutlined />}
            loading={submitting}
          >
            {t("profile.password.submit")}
          </Button>
        </Form>
      </Space>
    </Card>
  );
};

export default AuditorChangePassword;
